import { Head, Link, router, useForm } from "@inertiajs/react"
import {
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Edit2,
  Trash2,
  Send,
  Sparkles,
  Upload,
  X,
  Link2,
  Check,
  Mic,
  ExternalLink,
  RefreshCw,
} from "lucide-react"
import { useState, useCallback, useRef, useEffect } from "react"
import { CoachLayout } from "../components/CoachLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { RichTextEditor } from "@/components/ui/rich-text-editor"
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { MemberStorySourcePanel } from "@/components/member-story/MemberStorySourcePanel"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { AiChatPanel } from "@/components/ai/AiChatPanel"
import { useTourPage } from "@/components/tour/TourContext"
import { PodcastPanel } from "@/components/podcast/PodcastPanel"

// Must be rendered INSIDE CoachLayout (which provides TourProvider).
// Page-level hooks can't call useTourPage directly since CoachLayout is a child.
function ArticleTourSync({ setIsEditing, setAiPanelOpen, setActiveTab }) {
  const { registerTourHandlers } = useTourPage()
  useEffect(() => {
    return registerTourHandlers({ setIsEditing, setAiPanelOpen, setActiveTab })
  }, [registerTourHandlers, setIsEditing, setAiPanelOpen, setActiveTab])
  return null
}
import { US_STATES } from "@/lib/us-states"
import { COUNTRIES } from "@/lib/countries"

const statusConfig = {
  draft: { label: "Draft", variant: "secondary", icon: FileText },
  pending_review: { label: "Pending Review", variant: "warning", icon: Clock },
  published: { label: "Published", variant: "success", icon: CheckCircle },
  rejected: { label: "Rejected", variant: "destructive", icon: AlertCircle },
}

function StatusBadge({ status }) {
  const config = statusConfig[status] || statusConfig.draft
  return (
    <Badge
      variant={config.variant}
      className={
        config.variant === "warning"
          ? "bg-amber-100 text-amber-800 hover:bg-amber-100"
          : config.variant === "success"
          ? "bg-green-100 text-green-800 hover:bg-green-100"
          : ""
      }
    >
      {config.label}
    </Badge>
  )
}

function formatDate(dateString) {
  if (!dateString) return "-"
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

export default function ArticleShow({
  currentUser,
  article,
  categories,
  voices,
  errors,
  writingGuidelines,
  coachPreferences = {},
  memberStorySource = null,
}) {
  const [isEditing, setIsEditing] = useState(
    article.status === "draft" && !article.content?.trim()
  )
  const [aiPanelOpen, setAiPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState("article")
  const [selectedParagraph, setSelectedParagraph] = useState(null)
  const [editor, setEditor] = useState(null)

  const [imagePreview, setImagePreview] = useState(null)
  const fileInputRef = useRef(null)

  const { data, setData, patch, processing, transform } = useForm({
    title: article.title || "",
    slug: article.slug || "",
    content: article.content || "",
    excerpt: article.excerpt || "",
    category_id: article.categoryId?.toString() || "",
    featured_image_url: (!article.featuredImageAttached && article.featuredImageUrl) || "",
    featured_image: null,
    _purge_featured_image: false,
    meta_description: article.metaDescription || "",
    meta_keywords: article.metaKeywords || "",
    company: article.company || "",
    job_title: article.jobTitle || "",
    city: article.city || "",
    state: article.state || "",
    country: article.country || "US",
    interviewee_name: article.intervieweeName || "",
    interviewee_email: article.intervieweeEmail || "",
    interviewee_website: article.intervieweeWebsite || "",
    interviewee_twitter: article.intervieweeTwitter || "",
    interviewee_linkedin: article.intervieweeLinkedin || "",
    interviewee_facebook: article.intervieweeFacebook || "",
    interviewee_instagram: article.intervieweeInstagram || "",
    interviewee_youtube: article.intervieweeYoutube || "",
    podcast_enabled: article.podcastEnabled || false,
    podcast_style: article.podcastStyle || coachPreferences.favoritePodcastStyle || "",
    podcast_host_voice_id: article.podcastHostVoiceId?.toString() || coachPreferences.favoriteHostVoiceId?.toString() || "",
    podcast_interviewee_voice_id:
      article.podcastIntervieweeVoiceId?.toString() || coachPreferences.favoriteIntervieweeVoiceId?.toString() || "",
    podcast_additional_notes: article.podcastAdditionalNotes || "",
    podcast_host_name: article.podcastHostName || coachPreferences.name || "",
    podcast_interviewee_name: article.podcastIntervieweeName || article.intervieweeName || "",
    podcast_host_name_phonetic: article.podcastHostNamePhonetic || coachPreferences.namePhonetic || "",
    podcast_interviewee_name_phonetic: article.podcastIntervieweeNamePhonetic || "",
  })

  // Never send featured_image unless it's a real upload — a null value
  // reaches the controller as "" (Inertia coerces null to an empty string
  // in FormData), which the featured_image:"" purge bug then detached on
  // every save. Omitting the key entirely is the frontend defense; the
  // controller strips a blank value regardless as a backstop.
  useEffect(() => {
    transform((currentData) => {
      if (currentData.featured_image instanceof File) return currentData
      const { featured_image, ...rest } = currentData
      return rest
    })
  }, [transform])

  const handleSave = () => {
    patch(`/app/coach/articles/${article.id}`, {
      preserveScroll: true,
      forceFormData: true,
      onSuccess: () => {
        setIsEditing(false)
        setImagePreview(null)
        setData("_purge_featured_image", false)
      },
    })
  }

  const handleCancel = () => {
    setData({
      title: article.title || "",
      slug: article.slug || "",
      content: article.content || "",
      excerpt: article.excerpt || "",
      category_id: article.categoryId?.toString() || "",
      featured_image_url: (!article.featuredImageAttached && article.featuredImageUrl) || "",
      featured_image: null,
      _purge_featured_image: false,
      meta_description: article.metaDescription || "",
      meta_keywords: article.metaKeywords || "",
      company: article.company || "",
      job_title: article.jobTitle || "",
      city: article.city || "",
      state: article.state || "",
      country: article.country || "US",
      interviewee_name: article.intervieweeName || "",
      interviewee_email: article.intervieweeEmail || "",
      interviewee_website: article.intervieweeWebsite || "",
      interviewee_twitter: article.intervieweeTwitter || "",
      interviewee_linkedin: article.intervieweeLinkedin || "",
      interviewee_facebook: article.intervieweeFacebook || "",
      interviewee_instagram: article.intervieweeInstagram || "",
      interviewee_youtube: article.intervieweeYoutube || "",
      podcast_enabled: article.podcastEnabled || false,
      podcast_style: article.podcastStyle || coachPreferences.favoritePodcastStyle || "",
      podcast_host_voice_id: article.podcastHostVoiceId?.toString() || coachPreferences.favoriteHostVoiceId?.toString() || "",
      podcast_interviewee_voice_id:
        article.podcastIntervieweeVoiceId?.toString() || coachPreferences.favoriteIntervieweeVoiceId?.toString() || "",
      podcast_additional_notes: article.podcastAdditionalNotes || "",
      podcast_host_name: article.podcastHostName || coachPreferences.name || "",
      podcast_interviewee_name: article.podcastIntervieweeName || article.intervieweeName || "",
      podcast_host_name_phonetic: article.podcastHostNamePhonetic || coachPreferences.namePhonetic || "",
      podcast_interviewee_name_phonetic: article.podcastIntervieweeNamePhonetic || "",
    })
    setImagePreview(null)
    setIsEditing(false)
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setData((prev) => ({ ...prev, featured_image: file, featured_image_url: "", _purge_featured_image: false }))
      const reader = new FileReader()
      reader.onload = (e) => setImagePreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleUrlChange = (url) => {
    setData((prev) => ({ ...prev, featured_image_url: url, featured_image: null, _purge_featured_image: false }))
    setImagePreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const clearFeaturedImage = () => {
    setData((prev) => ({ ...prev, featured_image: null, featured_image_url: "", _purge_featured_image: true }))
    setImagePreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const currentImageUrl = imagePreview || data.featured_image_url || (article.featuredImageAttached && article.featuredImageUrl) || null

  const handleDelete = () => {
    router.delete(`/app/coach/articles/${article.id}`)
  }

  const handleRegenerate = () => {
    router.post(`/app/coach/articles/${article.id}/regenerate`)
  }

  const handleSubmitForReview = () => {
    router.post(`/app/coach/articles/${article.id}/submit_for_review`)
  }

  const handleParagraphSelect = useCallback((paragraph) => {
    setSelectedParagraph(paragraph)
    if (!aiPanelOpen) setAiPanelOpen(true)
  }, [aiPanelOpen])

  const toggleAiPanel = () => {
    setAiPanelOpen(!aiPanelOpen)
    if (aiPanelOpen) {
      setSelectedParagraph(null)
      editor?.commands.clearParagraphSelection?.()
    }
  }

  const podcastReadyForSubmit = article.podcastReadyForSubmit === true || !article.podcastEnabled

  const canSubmitForReview =
    (article.status === "draft" || article.status === "rejected") && podcastReadyForSubmit

  return (
    <CoachLayout currentUser={currentUser}>
      <ArticleTourSync setIsEditing={setIsEditing} setAiPanelOpen={setAiPanelOpen} setActiveTab={setActiveTab} />
      <Head title={`${article.title} - Kingdom Factor`} />

      <div className={aiPanelOpen ? "grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-0 -m-4 lg:-m-6 min-h-[calc(100vh-4rem)]" : ""}>
        {/* Main content area */}
        <div className={aiPanelOpen ? "p-4 lg:p-6 overflow-y-auto" : ""}>
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4">
              <Link
                href="/app/coach/articles"
                className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Articles
              </Link>

              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-zinc-900">
                      {article.title}
                    </h1>
                    <StatusBadge status={article.status} />
                  </div>
                  {article.rejectionReason && (
                    <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md">
                      <strong>Rejection reason:</strong> {article.rejectionReason}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {isEditing && (
                    <Button
                      data-tour="ai-btn"
                      variant={aiPanelOpen ? "outline" : "ghost"}
                      size="sm"
                      onClick={toggleAiPanel}
                      className={aiPanelOpen ? "border-kf-red text-kf-red" : "text-zinc-500"}
                    >
                      <Sparkles className="h-4 w-4" />
                      <span className="hidden sm:inline">AI</span>
                    </Button>
                  )}
                  {!isEditing ? (
                    <>
                      <Button variant="outline" onClick={() => setIsEditing(true)}>
                        <Edit2 className="h-4 w-4" />
                        Edit
                      </Button>
                      {article.canRegenerate && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline">
                              <RefreshCw className="h-4 w-4" />
                              Regenerate
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Regenerate Article</AlertDialogTitle>
                              <AlertDialogDescription>
                                This replaces the current draft with a fresh
                                AI-generated article from your original meeting
                                transcript. The current version will be
                                discarded. Continue?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={handleRegenerate}>
                                Regenerate
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" className="text-red-600">
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Article</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{article.title}"?
                              This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleDelete}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  ) : (
                    <>
                      <Button variant="ghost" onClick={handleCancel}>
                        Cancel
                      </Button>
                      <Button
                        variant="gradient"
                        onClick={handleSave}
                        disabled={processing}
                      >
                        {processing ? "Saving..." : "Save Changes"}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="article">Article</TabsTrigger>
                <TabsTrigger data-tour="podcast-tab" value="podcast">Podcast</TabsTrigger>
              </TabsList>

              {/* Article Tab */}
              <TabsContent value="article" className="mt-6">
                <Card>
                  <CardContent className="p-6 space-y-6">
                    {/* Basic Info */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="title">Title</Label>
                        <Input
                          id="title"
                          value={data.title}
                          onChange={(e) => setData("title", e.target.value)}
                          disabled={!isEditing}
                          className={errors?.title ? "border-red-500" : ""}
                        />
                        {errors?.title && (
                          <p className="text-xs text-red-500">{errors.title}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="slug">URL Slug</Label>
                        <Input
                          id="slug"
                          value={data.slug}
                          onChange={(e) => setData("slug", e.target.value)}
                          disabled={!isEditing}
                          className={errors?.slug ? "border-red-500" : ""}
                        />
                        {errors?.slug && (
                          <p className="text-xs text-red-500">{errors.slug}</p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="category">Category</Label>
                      <Select
                        value={data.category_id}
                        onValueChange={(value) => setData("category_id", value)}
                        disabled={!isEditing}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories?.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id.toString()}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Featured Image */}
                    <div data-tour="featured-image-upload" className="space-y-3">
                      <Label>Featured Image</Label>
                      {currentImageUrl && (
                        <div className="relative aspect-[2/1] rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50">
                          <img
                            src={currentImageUrl}
                            alt="Featured image preview"
                            className="w-full h-full object-cover"
                          />
                          {isEditing && (
                            <button
                              type="button"
                              onClick={clearFeaturedImage}
                              className="absolute top-2 right-2 p-1 bg-white/80 hover:bg-white rounded-full shadow"
                            >
                              <X className="h-4 w-4 text-zinc-600" />
                            </button>
                          )}
                        </div>
                      )}
                      {isEditing && (
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <Upload className="h-4 w-4" />
                              Upload Image
                            </Button>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/webp,image/png,image/jpeg,image/gif"
                              onChange={handleFileChange}
                              className="hidden"
                            />
                            <p className="text-xs text-zinc-500">
                              Recommended: 2:1 ratio, WebP format.{" "}
                              <a
                                href="https://cloudconvert.com/png-to-webp"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-kf-red underline"
                              >
                                Convert to WebP
                              </a>
                            </p>
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="featured_image_url" className="text-xs text-zinc-500">
                              Or paste image URL
                            </Label>
                            <Input
                              id="featured_image_url"
                              value={data.featured_image_url}
                              onChange={(e) => handleUrlChange(e.target.value)}
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="excerpt">Excerpt</Label>
                      <Textarea
                        id="excerpt"
                        value={data.excerpt}
                        onChange={(e) => setData("excerpt", e.target.value)}
                        disabled={!isEditing}
                        rows={2}
                        placeholder="Brief summary of the article (max 300 characters)"
                        className={errors?.excerpt ? "border-red-500" : ""}
                      />
                      <p className="text-xs text-zinc-500">
                        {data.excerpt?.length || 0}/300 characters
                      </p>
                      {errors?.excerpt && (
                        <p className="text-xs text-red-500">{errors.excerpt}</p>
                      )}
                    </div>

                    {/* Interviewee Information */}
                    <div data-tour="interviewee-section">
                      <h3 className="text-sm font-semibold text-zinc-900 mb-1">
                        Interviewee Information
                      </h3>
                      <p className="text-xs text-zinc-500 mb-4">
                        Information about the person featured in this article. Company and Job Title are required for review submission.
                      </p>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="company">
                            Company <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="company"
                            value={data.company}
                            onChange={(e) => setData("company", e.target.value)}
                            disabled={!isEditing}
                            placeholder="Interviewee's company"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="job_title">
                            Job Title <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="job_title"
                            value={data.job_title}
                            onChange={(e) => setData("job_title", e.target.value)}
                            disabled={!isEditing}
                            placeholder="Interviewee's job title"
                          />
                        </div>
                      </div>
                      <div className="space-y-2 mt-4">
                        <Label htmlFor="country">Country</Label>
                        <select
                          id="country"
                          value={data.country}
                          onChange={(e) => {
                            const newCountry = e.target.value
                            if (newCountry !== data.country) {
                              setData((prev) => ({ ...prev, country: newCountry, state: "" }))
                            }
                          }}
                          disabled={!isEditing}
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-70"
                        >
                          {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="city">City</Label>
                          <Input
                            id="city"
                            value={data.city}
                            onChange={(e) => setData("city", e.target.value)}
                            disabled={!isEditing}
                            placeholder="e.g. Dallas"
                          />
                        </div>
                        {data.country === "US" ? (
                          <div className="space-y-2">
                            <Label htmlFor="state">State</Label>
                            <Select
                              value={data.state || ""}
                              onValueChange={(value) => setData("state", value)}
                              disabled={!isEditing}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select state" />
                              </SelectTrigger>
                              <SelectContent>
                                {US_STATES.map((s) => (
                                  <SelectItem key={s.value} value={s.value}>
                                    {s.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Label htmlFor="state">State / Province</Label>
                            <Input
                              id="state"
                              value={data.state}
                              onChange={(e) => setData("state", e.target.value)}
                              disabled={!isEditing}
                              placeholder="State or province"
                            />
                          </div>
                        )}
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_name">Interviewee Name</Label>
                          <Input
                            id="interviewee_name"
                            value={data.interviewee_name}
                            onChange={(e) => setData("interviewee_name", e.target.value)}
                            disabled={!isEditing}
                            placeholder="Interviewee's full name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_email">Interviewee Email</Label>
                          <Input
                            id="interviewee_email"
                            type="email"
                            value={data.interviewee_email}
                            onChange={(e) => setData("interviewee_email", e.target.value)}
                            disabled={!isEditing}
                            placeholder="interviewee@example.com"
                          />
                        </div>
                      </div>
                      <div data-tour="interviewee-social-links" className="grid gap-4 sm:grid-cols-2 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_website">Website</Label>
                          <Input
                            id="interviewee_website"
                            value={data.interviewee_website}
                            onChange={(e) => setData("interviewee_website", e.target.value)}
                            disabled={!isEditing}
                            placeholder="https://example.com"
                            className={errors?.interviewee_website ? "border-red-500" : ""}
                          />
                          {errors?.interviewee_website && (
                            <p className="text-xs text-red-500">{errors.interviewee_website[0]}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_linkedin">LinkedIn</Label>
                          <Input
                            id="interviewee_linkedin"
                            value={data.interviewee_linkedin}
                            onChange={(e) => setData("interviewee_linkedin", e.target.value)}
                            disabled={!isEditing}
                            placeholder="https://linkedin.com/in/..."
                            className={errors?.interviewee_linkedin ? "border-red-500" : ""}
                          />
                          {errors?.interviewee_linkedin && (
                            <p className="text-xs text-red-500">{errors.interviewee_linkedin[0]}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_twitter">Twitter / X</Label>
                          <Input
                            id="interviewee_twitter"
                            value={data.interviewee_twitter}
                            onChange={(e) => setData("interviewee_twitter", e.target.value)}
                            disabled={!isEditing}
                            placeholder="https://x.com/..."
                            className={errors?.interviewee_twitter ? "border-red-500" : ""}
                          />
                          {errors?.interviewee_twitter && (
                            <p className="text-xs text-red-500">{errors.interviewee_twitter[0]}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_facebook">Facebook</Label>
                          <Input
                            id="interviewee_facebook"
                            value={data.interviewee_facebook}
                            onChange={(e) => setData("interviewee_facebook", e.target.value)}
                            disabled={!isEditing}
                            placeholder="https://facebook.com/..."
                            className={errors?.interviewee_facebook ? "border-red-500" : ""}
                          />
                          {errors?.interviewee_facebook && (
                            <p className="text-xs text-red-500">{errors.interviewee_facebook[0]}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_instagram">Instagram</Label>
                          <Input
                            id="interviewee_instagram"
                            value={data.interviewee_instagram}
                            onChange={(e) => setData("interviewee_instagram", e.target.value)}
                            disabled={!isEditing}
                            placeholder="https://instagram.com/..."
                            className={errors?.interviewee_instagram ? "border-red-500" : ""}
                          />
                          {errors?.interviewee_instagram && (
                            <p className="text-xs text-red-500">{errors.interviewee_instagram[0]}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="interviewee_youtube">YouTube</Label>
                          <Input
                            id="interviewee_youtube"
                            value={data.interviewee_youtube}
                            onChange={(e) => setData("interviewee_youtube", e.target.value)}
                            disabled={!isEditing}
                            placeholder="https://youtube.com/..."
                            className={errors?.interviewee_youtube ? "border-red-500" : ""}
                          />
                          {errors?.interviewee_youtube && (
                            <p className="text-xs text-red-500">{errors.interviewee_youtube[0]}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div data-tour="article-body" className="space-y-2">
                      <Label htmlFor="content">Content</Label>
                      <RichTextEditor
                        value={data.content}
                        onChange={(html) => setData("content", html)}
                        placeholder="Write your article content..."
                        editable={isEditing}
                        aiPanelOpen={aiPanelOpen && isEditing}
                        onParagraphSelect={handleParagraphSelect}
                        onEditorReady={setEditor}
                      />
                      {errors?.content && (
                        <p className="text-xs text-red-500">{errors.content}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Podcast Tab */}
              <TabsContent value="podcast" className="mt-6">
                <PodcastPanel
                  article={article}
                  data={data}
                  setData={setData}
                  isEditing={isEditing}
                  voices={voices}
                  isAdmin={false}
                  baseUrl="/app/coach"
                  coachPreferences={coachPreferences}
                />
              </TabsContent>
            </Tabs>

            {/* Bottom Save Button */}
            {isEditing && (
              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  variant="gradient"
                  onClick={handleSave}
                  disabled={processing}
                >
                  {processing ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            )}

            {/* Submit for Review */}
            {canSubmitForReview && (
              <Card data-tour="submit-review" className="border-amber-200 bg-amber-50">
                <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-zinc-900">Ready to submit?</h3>
                    <p className="text-sm text-zinc-600">
                      Submit your article for review by an admin. You'll be notified
                      once it's reviewed.
                    </p>
                  </div>
                  <Button variant="gradient" onClick={handleSubmitForReview}>
                    <Send className="h-4 w-4" />
                    Submit for Review
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Podcast setup required before submit */}
            {(article.status === "draft" || article.status === "rejected") && !podcastReadyForSubmit && (
              <Card data-tour="submit-review" className="border-purple-200 bg-purple-50">
                <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-zinc-900 flex items-center gap-2">
                      <Mic className="h-4 w-4 text-purple-600" />
                      Complete podcast setup to submit
                    </h3>
                    <p className="text-sm text-zinc-600">
                      You've enabled a podcast for this article. Generate and review your
                      podcast script on the{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab("podcast")
                          window.scrollTo({ top: 0, behavior: "smooth" })
                        }}
                        className="text-purple-600 font-medium underline hover:text-purple-800"
                      >
                        Podcast tab
                      </button>{" "}
                      before submitting, or disable the podcast to submit without one.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Share Preview Link */}
            {article.status !== "published" && article.previewToken && (
              <CopyPreviewLinkCard previewToken={article.previewToken} />
            )}

            {/* View Published Article */}
            {article.status === "published" && article.categorySlug && article.slug && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-zinc-900">Article is live</h3>
                    <p className="text-sm text-zinc-600">
                      Your article is published. View it on the public Collective.
                    </p>
                  </div>
                  <Button
                    variant="kf-outline"
                    asChild
                  >
                    <a
                      href={`/collective/${article.categorySlug}/${article.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View Published Article
                    </a>
                  </Button>
                </CardContent>
              </Card>
            )}

            <MemberStorySourcePanel source={memberStorySource} />

            {/* Article Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Article Information</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <dt className="text-zinc-500">Created</dt>
                    <dd>{formatDate(article.createdAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500">Updated</dt>
                    <dd>{formatDate(article.updatedAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500">Submitted</dt>
                    <dd>{formatDate(article.submittedAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500">Published</dt>
                    <dd>{formatDate(article.publishedAt)}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* AI Chat Panel */}
        {aiPanelOpen && isEditing && (
          <div data-tour="ai-panel" className="hidden lg:block h-[calc(100vh-4rem)] sticky top-16">
            <AiChatPanel
              articleId={article.id}
              baseUrl="/app/coach"
              editor={editor}
              selectedParagraph={selectedParagraph}
              setSelectedParagraph={(p) => {
                setSelectedParagraph(p)
                if (!p) editor?.commands.clearParagraphSelection?.()
              }}
              onClose={toggleAiPanel}
            />
          </div>
        )}
      </div>
    </CoachLayout>
  )
}

function CopyPreviewLinkCard({ previewToken }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    const previewUrl = `${window.location.origin}/collective/preview/${previewToken}`

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(previewUrl).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }).catch(() => {
        fallbackCopy(previewUrl)
      })
    } else {
      fallbackCopy(previewUrl)
    }
  }

  const fallbackCopy = (text) => {
    const textarea = document.createElement("textarea")
    textarea.value = text
    textarea.style.position = "fixed"
    textarea.style.opacity = "0"
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand("copy")
    document.body.removeChild(textarea)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Card>
      <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="font-semibold text-zinc-900">Share for approval</h3>
          <p className="text-sm text-zinc-600">
            Copy a preview link to send to your interviewee for approval before publishing.
          </p>
        </div>
        <Button variant="outline" onClick={handleCopy} className="flex-shrink-0">
          {copied ? (
            <>
              <Check className="h-4 w-4" />
              Copied!
            </>
          ) : (
            <>
              <Link2 className="h-4 w-4" />
              Copy Preview Link
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  )
}
